import { useMemo, useState } from 'react';
import TimeSeriesChart from './TimeSeriesChart';

const div = (a, b) => (a != null && b ? a / b : null);

// Orden pedido: Revenue - Órdenes - Ticket - CR - Sesiones - Inversión.
const METRICS = [
  { key: 'revenue', label: 'Revenue', format: 'currency', color: '#6366f1', compute: (d) => d.revenue },
  { key: 'orders', label: 'Órdenes', format: 'number', color: '#0ea5e9', compute: (d) => d.orders },
  { key: 'avg_ticket', label: 'Ticket', format: 'currency', color: '#10b981', compute: (d) => div(d.revenue, d.orders) },
  { key: 'conversion_rate', label: 'CR', format: 'percent', color: '#f59e0b', compute: (d) => div(d.orders, d.sessions) },
  { key: 'sessions', label: 'Sesiones', format: 'number', color: '#8b5cf6', compute: (d) => d.sessions },
  { key: 'spend', label: 'Inversión', format: 'currency', color: '#f43f5e', compute: (d) => d.spend },
  { key: 'pct_mkt', label: '% Mkt', format: 'percent', color: '#14b8a6', compute: (d) => div(d.spend, d.revenue) },
];

const META = Object.fromEntries(METRICS.map((m) => [m.key, m]));
const MAX = 2;

/**
 * Gráfico principal dinámico del panel ecom: selector de hasta 2 métricas
 * (revenue/órdenes/ticket/CR/sesiones/inversión) sobre el look de área. Con 2
 * métricas usa doble eje. Siempre queda al menos una seleccionada.
 */
export default function MetricExplorerChart({ daily }) {
  const [selected, setSelected] = useState(['revenue']);

  const data = useMemo(
    () =>
      daily.map((d) => {
        const row = { date: d.date };
        METRICS.forEach((m) => {
          row[m.key] = m.compute(d);
        });
        return row;
      }),
    [daily]
  );

  const series = selected.map((key, i) => ({
    dataKey: key,
    label: META[key].label,
    color: META[key].color,
    format: META[key].format,
    yAxisId: i === 0 ? 'left' : 'right',
  }));

  const toggle = (key) =>
    setSelected((prev) => {
      if (prev.includes(key)) {
        return prev.length === 1 ? prev : prev.filter((k) => k !== key); // siempre ≥1
      }
      return prev.length < MAX ? [...prev, key] : [prev[prev.length - 1], key]; // reemplaza la más vieja
    });

  return (
    <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap gap-1.5">
        {METRICS.map((m) => {
          const active = selected.includes(m.key);
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => toggle(m.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                active
                  ? 'border-transparent text-white'
                  : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
              }`}
              style={active ? { backgroundColor: m.color } : undefined}
            >
              {!active && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: m.color }} />}
              {m.label}
            </button>
          );
        })}
      </div>
      <div className="min-h-[260px] flex-1">
        <TimeSeriesChart data={data} series={series} showLegend={false} card={false} height="100%" />
      </div>
    </div>
  );
}
